import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu, X, Phone } from 'lucide-react';
import { SERVICES, CONTACT } from '@/content/site';

const LOGO = '/images/ktk-logo-tight.png';

export default function Header() {
  const [ddOpen, setDdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const onServicePath =
    location.pathname.startsWith('/solutions') && location.pathname !== '/solutions';

  if (mobileOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }

  const navCls = ({ isActive }) => 'nav-link' + (isActive ? ' is-active' : '');

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50"
        style={{
          background: 'rgba(7, 29, 24, 0.82)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(178, 139, 67, 0.18)',
        }}
      >
        <div className="wrap flex items-center justify-between h-[78px]">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center"
            aria-label="KTK Advisors — Home"
          >
            <span className="logo-chip">
              <img src={LOGO} alt="KTK Advisors" width="1284" height="386" className="h-8 sm:h-9 w-auto" />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
            <NavLink to="/about" className={navCls} data-testid="nav-about-link">
              About
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setDdOpen(true)}
              onMouseLeave={() => setDdOpen(false)}
              onKeyDown={(e) => e.key === 'Escape' && setDdOpen(false)}
            >
              <NavLink
                to="/solutions"
                className={({ isActive }) =>
                  'nav-link' + (isActive || onServicePath ? ' is-active' : '')
                }
                data-testid="nav-solutions-link"
                aria-haspopup="true"
                aria-expanded={ddOpen}
                onFocus={() => setDdOpen(true)}
              >
                Solutions
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${ddOpen ? 'rotate-180' : ''}`}
                />
              </NavLink>
              {ddOpen && (
                <div className="header-dropdown" role="menu">
                  <div className="header-dropdown__panel">
                    <Link to="/solutions" data-testid="nav-dd-overview" className="dd-item" role="menuitem">
                      <span className="n">S</span>
                      <span className="t">Solutions Overview</span>
                    </Link>
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={s.path}
                        data-testid={`nav-dd-${s.slug}`}
                        className={'dd-item' + (location.pathname === s.path ? ' is-active' : '')}
                        role="menuitem"
                      >
                        <span className="n">{s.num}</span>
                        <span className="t">{s.title}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/our-approach" className={navCls} data-testid="nav-approach-link">
              Our Approach
            </NavLink>
            <NavLink to="/contact" className={navCls} data-testid="nav-contact-link">
              Contact
            </NavLink>
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={CONTACT.phoneHref}
              data-testid="header-phone-link"
              className="hidden md:flex items-center gap-2 text-[14px] font-semibold text-ivory/85 hover:text-goldbright transition-colors"
            >
              <Phone size={15} />
              {CONTACT.phone}
            </a>
            <Link
              to="/contact?interest=not-sure"
              data-testid="header-schedule-consultation-button"
              className="btn btn-gold !py-3 !px-6 text-[13px] hidden sm:inline-flex"
            >
              Schedule a Consultation
            </Link>
            <button
              type="button"
              data-testid="header-mobile-menu-button"
              className="lg:hidden p-2 text-ivory"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-drawer"
            data-testid="mobile-drawer"
            className="fixed inset-0 z-40 bg-deep overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="aura aura--gold" style={{ width: 340, height: 340, top: -80, right: -80 }} />
            <div className="wrap pt-32 pb-16 relative">
              <nav aria-label="Mobile">
                <Link to="/about" data-testid="mobile-about-link" className="drawer-link" onClick={() => setMobileOpen(false)}>
                  About
                </Link>
                <Link to="/solutions" data-testid="mobile-solutions-link" className="drawer-link" onClick={() => setMobileOpen(false)}>
                  Solutions
                </Link>
                <div className="py-2">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={s.path}
                      data-testid={`mobile-service-${s.slug}`}
                      className="drawer-sub flex items-center gap-3"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="font-mono text-[10px] text-goldbright">{s.num}</span>
                      {s.title}
                    </Link>
                  ))}
                </div>
                <Link to="/our-approach" data-testid="mobile-approach-link" className="drawer-link" onClick={() => setMobileOpen(false)}>
                  Our Approach
                </Link>
                <Link to="/contact" data-testid="mobile-contact-link" className="drawer-link" onClick={() => setMobileOpen(false)}>
                  Contact
                </Link>
              </nav>
              <div className="mt-10 flex flex-col gap-4">
                <Link
                  to="/contact?interest=not-sure"
                  data-testid="mobile-schedule-consultation-button"
                  className="btn btn-gold w-full"
                  onClick={() => setMobileOpen(false)}
                >
                  Schedule a Consultation
                </Link>
                <a href={CONTACT.phoneHref} data-testid="mobile-phone-link" className="text-center text-[15px] font-semibold text-ivory/75">
                  {CONTACT.phone}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
